import React, { forwardRef, useRef, useState } from "react";
import { useReactToPrint } from "react-to-print";
import calculateAge from "../../../utils/calculateAge";
import { MedicationName } from "../../MedicationName";
import Spinner from "../../spinners/Spinner";
import instance from "../../../axios/instance";
import { getToken } from "../../../utils/token";
import { toast } from "react-toastify";
import Barcode from "react-barcode";
import PaymentCardInfoModal from "./PaymentCardModal";

const renderMedicationDirections = (medication) => {
  if (Array.isArray(medication.directions) && medication.directions.length > 0) {
    return (
      <div className="space-y-1">
        {medication.directions.map((direction, index) => (
          <div key={`${direction.month || index}-${direction.label || "direction"}`}>
            <span className="font-medium">
              {direction.label || `Month ${index + 1}`}:
            </span>{" "}
            <span>{direction.text || "-/-"}</span>
          </div>
        ))}
      </div>
    );
  }

  return medication.direction || "-/-";
};

export const OrderSummaryModal = ({
  onClose,
  order,
  isRefill,
  showNextSteps,
}) => {
  const componentRef = useRef();
  const [loading, setLoading] = useState(false);
  const [openCardModal, setOpenCardModal] = useState(false);
  const [success, setSuccess] = useState(false);
  const [invoiceGenerated, setInvoiceGenerated] = useState(order.mx_invoice_id);
  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
  });
  const isPatientOrder = order.order_method === "Patient";

  const handleGenerateInvoice = async () => {
    if (invoiceGenerated) {
      setOpenCardModal(true);
      return;
    }

    if (order.invoice_url) {
      window.open(order.invoice_url, "_blank");
      return;
    }

    try {
      setLoading(true);
      const token = getToken();
      const res = await instance.post(
        "/doctor/reps/orders/payment-invoice",
        {
          orderId: order.id,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      setLoading(false);
      if (res.data.url) {
        window.open(res.data.url, "_blank");
      } else {
        setOpenCardModal(true);
        setInvoiceGenerated(true);
      }
    } catch (error) {
      setLoading(false);
      toast.error(
        error.response
          ? error.response.data?.message
          : "An error occurred, please try again later",
      );
    }
  };

  return (
    <>
      {openCardModal && (
        <PaymentCardInfoModal
          onClose={() => setOpenCardModal(null)}
          open={openCardModal}
          address={order.delivery_address}
          zipcode={order.delivery_zipcode}
          orderId={order.id}
          facilityId={order.facility_id}
          triggerRevalidate={() => setSuccess(true)}
        />
      )}
      <div className="absolute top-0 h-[100vh] bottom-0 bg-white z-50 right-0 left-0 overflow-y-scroll py-5">
        <div className="flex justify-between items-end flex-wrap px-7">
          <button
            className="text-xs w-[150px] h-[40px] flex items-center justify-center bg-[tomato] text-white rounded-md py-2 px-2 hover:opacity-80 duration-200"
            onClick={onClose}
          >
            Close
          </button>
          <div className="flex gap-2">
            <button
              className="text-xs md:w-full xl:w-[150px] h-[40px] flex items-center justify-center bg-primary text-white rounded-md py-2 px-2 hover:opacity-80 duration-200"
              onClick={handlePrint}
            >
              Download PDF
            </button>
          </div>
        </div>
        {isPatientOrder ? (
          <PatientSummaryOrder order={order} ref={componentRef} />
        ) : (
          <BulkAutoSummaryOrder order={order} ref={componentRef} />
        )}
        {showNextSteps && !success && (
          <div className="border-t px-7 py-5 flex justify-between align-center">
            <span className="block text-xl font-bold">Next Steps</span>
            <button
              className="text-xs w-[150px] h-[40px] flex items-center justify-center bg-primary text-white rounded-md py-2 px-2 hover:opacity-80 duration-200 disabled:opacity-70 disabled:cursor-not-allowed"
              onClick={handleGenerateInvoice}
              disabled={order.payment_status.toLowerCase() === "refunded"}
            >
              {loading ? <Spinner color={"white"} /> : "Make Payment"}
            </button>
          </div>
        )}
      </div>
    </>
  );
};

const PatientSummaryOrder = forwardRef(({ order }, ref) => {
  const { patient, facility, doctor, pharmacy } = order;
  const medications = JSON.parse(order.medication);
  return (
    <div className="bg-white px-7 relative" ref={ref}>
      <div className="top-4 absolute">
        {order?.id && (
          <Barcode
            value={order?.id}
            width={1.5}
            height={30}
            fontSize={4}
            margin={0}
            displayValue={false}
          />
        )}
      </div>
      <div className="mt-3 mb-3 text-center font-bold rounded-md text-5xl">
        {facility.name}
      </div>
      <div className="text-center text-sm opacity-80">
        <strong className="font-semibold">Address:</strong>
        <span className="ml-2">
          {facility.address1} {facility.state}. {facility.country}.
        </span>
      </div>
      <div className="text-center text-sm opacity-80 my-1">
        <strong className="font-semibold">Phone:</strong>
        <span className="ml-2">{facility.office_phone}</span>
      </div>
      <div className="text-center text-sm opacity-80 mt-1">
        <strong className="font-semibold">Email:</strong>
        <span className="ml-2">{facility.email}</span>
      </div>
      <div className="mt-7">
        <div className="flex justify-between items-center">
          <div>
            <div className="text-base text-[black] font-semibold mb-1">
              Supervising Practitioner:
            </div>
            <div className="text-2xl font-bold">
              {doctor.prefix ? `${doctor.prefix}.` : ""}{" "}
              {doctor.lastname + " " + doctor.firstname}
            </div>
          </div>
          <div>
            <div className="text-base text-right text-[black] font-semibold mb-1">
              NPI Number:
              <span className="ml-2 font-medium text-sm">
                {doctor.npi_number}
              </span>
            </div>
            <div className="text-base text-right text-[black] font-semibold mb-1">
              DEA Number:
              <span className="ml-2 font-medium text-sm">
                {doctor.dea_number}
              </span>
            </div>
            <div className="text-base text-[black] text-right font-semibold mb-1">
              License:
              <span className="ml-2 font-medium text-sm">
                {doctor.license_number}
              </span>
            </div>
            {/* <div className="text-sm font-medium"></div> */}
          </div>
        </div>
        <div className="mt-3 text-center font-medium rounded-md text-xl">
          Electronic Prescription Order
        </div>

        <div className="mt-3 grid grid-cols-3 gap-x-5 gap-y-5 pt-3">
          <div>
            <div className="flex gap-x-3 mb-1">
              <div className="text-sm font-semibold mb-1 capitalize">
                Full Name:
              </div>
              <div className="text-sm">
                {patient.lastname + " " + patient.firstname}
              </div>
            </div>
            <div className="flex gap-x-3 mb-1">
              <div className="text-sm font-semibold mb-1 capitalize">
                Gender:
              </div>
              <div className="text-sm">
                {patient.gender ? patient.gender : "--"}
              </div>
            </div>
            <div className="flex gap-x-3 mb-1">
              <div className="text-sm font-semibold mb-1 capitalize">
                Patient ID:
              </div>
              <div className="text-sm">{patient.id ? patient.id : "--"}</div>
            </div>
            <div className="flex gap-x-3 mb-1">
              <div className="text-sm font-semibold mb-1 capitalize">
                Birth Sex:
              </div>
              <div className="text-sm">
                {patient.birth_sex ? patient.birth_sex : "--"}
              </div>
            </div>
            <div className="flex gap-x-3 mb-1">
              <div className="text-sm font-semibold mb-1 capitalize">
                Email:
              </div>
              <div className="text-sm">
                {patient.email ? patient.email : "--"}
              </div>
            </div>
            <div className="flex gap-x-3 mb-1">
              <div className="text-sm font-semibold mb-1 capitalize">
                Phone:
              </div>
              <div className="text-sm">
                {patient.phone ? patient.phone : "--"}
              </div>
            </div>
          </div>
          <div>
            <div className="flex gap-x-3 mb-1">
              <div className="text-sm font-semibold mb-1 capitalize">
                D.O.B:
              </div>
              <div className="text-sm">
                {patient.dob
                  ? patient.dob.split(",")[0].split("/").join("-")
                  : "--"}
              </div>
            </div>
            <div className="flex gap-x-2 mb-1">
              <div className="text-sm font-semibold mb-1 capitalize">City:</div>
              <div className="text-sm">
                {patient.city ? patient.city : "--"}
              </div>
            </div>
            <div className="flex gap-x-2 mb-1">
              <div className="text-sm font-semibold mb-1 capitalize">
                State:
              </div>
              <div className="text-sm">
                {patient.state ? patient.state : "--"}
              </div>
            </div>
            <div className="flex gap-x-2 mb-1">
              <div className="text-sm font-semibold mb-1 capitalize">
                Country:
              </div>
              <div className="text-sm">
                {patient.country ? patient.country : "--"}
              </div>
            </div>
            <div className="flex gap-x-2 mb-1">
              <div className="text-sm font-semibold mb-1 capitalize">
                Zip Code:
              </div>
              <div className="text-sm">
                {patient.zipcode ? patient.zipcode : "--"}
              </div>
            </div>
            <div className="flex gap-x-2 mb-1">
              <div className="text-sm font-semibold mb-1 capitalize">
                Address:
              </div>
              <div className="text-sm">
                {patient.address1 ? patient.address1 : "--"}
              </div>
            </div>
          </div>
          <div className="">
            <div className="flex gap-x-2 mb-1">
              <div className="text-sm font-semibold mb-1 capitalize">
                Order ID:
              </div>
              <div className="text-sm">{order.id}</div>
            </div>
            <div className="flex gap-x-2 mb-1">
              <div className="text-sm font-semibold mb-1 capitalize">
                Shipping Service:
              </div>
              <div className={`text-sm capitalize`}>
                {order.shipping_service
                  ? JSON.parse(order.shipping_service).name
                  : "--"}
              </div>
            </div>
            <div className="flex gap-x-2 mb-1">
              <div className="text-sm font-semibold mb-1 capitalize">
                Order Type:
              </div>
              <div className={`text-sm capitalize`}>{order.order_type}</div>
            </div>
            <div className="flex gap-x-2 mb-1">
              <div className="text-sm font-semibold mb-1 capitalize">
                Order Date:
              </div>
              <div className={`text-sm capitalize`}>
                {new Date(order.created_at).toLocaleDateString()}
              </div>
            </div>
            <div className="flex gap-x-2 mb-1">
              <div className="text-sm font-semibold mb-1 capitalize">
                Delivery Type:
              </div>
              <div className={`text-sm capitalize`}>{order.delivery_type}</div>
            </div>
            <div className="flex gap-x-2 mb-1">
              <div className="text-sm font-semibold mb-1 capitalize">
                Delivery Address:
              </div>
              <div className={`text-sm capitalize`}>
                {order.delivery_address}, {order.delivery_city}.{" "}
                {order.delivery_state}
              </div>
            </div>
            <div className="flex gap-x-2 mb-1">
              <div className="text-sm font-semibold mb-1 capitalize">
                Delivery Phone:
              </div>
              <div className={`text-sm capitalize`}>{order.delivery_phone}</div>
            </div>
          </div>
        </div>
      </div>
      <MedicationSummary
        medications={medications}
        orderMethod={order.order_method}
        medType={order.med_type}
      />
      <div className="border-t-2 border-[#ccc] pt-3">
        <div className="text-sm">
          This order has been sent to the{" "}
          <span className="font-semibold">{pharmacy.pharmacy_name}</span>{" "}
          Pharmacy:
        </div>
        <div className="flex gap-x-2 mb-1">
          <div className="text-sm font-semibold capitalize">Address:</div>
          <div className={`text-sm capitalize`}>{pharmacy.address}.</div>
          <div className="text-sm font-semibold capitalize">Phone:</div>
          <div className={`text-sm capitalize`}>{pharmacy.phone}</div>
          <div className="text-sm font-semibold capitalize">Email:</div>
          <div className={`text-sm`}>{pharmacy.email}</div>
        </div>
      </div>
      <div className="mt-5 mb-1 text-center font-bold rounded-md text-xl">
        {facility.name}
      </div>
      <div className="mb-5 text-center text-sm opacity-80">
        {facility.address1} {facility.city}, {facility.state}.{" "}
        {facility.country}. {facility.zipcode}
      </div>
    </div>
  );
});

const BulkAutoSummaryOrder = forwardRef(({ order }, ref) => {
  const { doctor, facility, pharmacy } = order;
  const medications = JSON.parse(order.medication);
  return (
    <div className="bg-white px-7" ref={ref}>
      <div className="mt-3 mb-3 text-center font-bold rounded-md text-5xl">
        {facility.name}
      </div>
      <div className="text-center text-sm opacity-80">
        <strong className="font-semibold">Address:</strong>
        <span className="ml-2">
          {facility.address1} {facility.state}. {facility.country}.
        </span>
      </div>
      <div className="text-center text-sm opacity-80 my-1">
        <strong className="font-semibold">Phone:</strong>
        <span className="ml-2">{facility.office_phone}</span>
      </div>
      <div className="text-center text-sm opacity-80 mt-1">
        <strong className="font-semibold">Email:</strong>
        <span className="ml-2">{facility.email}</span>
      </div>
      <div className="mt-7">
        <div className="flex justify-between items-center">
          <div>
            <div className="text-base text-[black] font-semibold mb-1">
              Supervising Practitioner:
            </div>
            <div className="text-2xl font-bold">
              {doctor.prefix ? `${doctor.prefix}.` : ""}{" "}
              {doctor.lastname + " " + doctor.firstname}
            </div>
          </div>
          <div>
            <div className="text-base text-right text-[black] font-semibold mb-1">
              NPI Number:
              <span className="ml-2 font-medium text-sm">
                {doctor.npi_number}
              </span>
            </div>
            <div className="text-base text-right text-[black] font-semibold mb-1">
              DEA Number:
              <span className="ml-2 font-medium text-sm">
                {doctor.dea_number}
              </span>
            </div>
            <div className="text-base text-[black] text-right font-semibold mb-1">
              License:
              <span className="ml-2 font-medium text-sm">
                {doctor.license_number}
              </span>
            </div>
            {/* <div className="text-sm font-medium"></div> */}
          </div>
        </div>
        <div className="mt-3 text-center font-medium rounded-md text-xl">
          Electronic Prescription Bulk Order
        </div>

        <div className="mt-3 grid grid-cols-2 gap-x-5 gap-y-5 pt-3">
          <div className="grid grid-cols-2 w-full gap-y-1">
            <div className="text-sm font-semibold mb-1 capitalize">
              Order ID
            </div>
            <div className="text-sm">{order.id}</div>
            <div className="text-sm font-semibold mb-1 capitalize">
              Shipping Service
            </div>
            <div className={`text-sm capitalize`}>
              {order.shipping_service
                ? JSON.parse(order.shipping_service).name
                : "--"}
            </div>
            <div className="text-sm font-semibold mb-1 capitalize">
              Order Type
            </div>
            <div className={`text-sm capitalize`}>{order.order_type}</div>
            <div className="text-sm font-semibold mb-1 capitalize">
              Order Date
            </div>
            <div className={`text-sm capitalize`}>
              {new Date(order.created_at).toLocaleDateString()}
            </div>
          </div>
          <div className="grid grid-cols-2 w-full gap-y-1">
            <div className="text-sm font-semibold mb-1 capitalize">
              Delivery Type
            </div>
            <div className={`text-sm capitalize`}>{order.delivery_type}</div>
            <div className="text-sm font-semibold mb-1 capitalize">
              Delivery Address
            </div>
            <div className={`text-sm capitalize`}>
              {order.delivery_address}, {order.delivery_city}.{" "}
              {order.delivery_state}
            </div>
            <div className="text-sm font-semibold mb-1 capitalize">
              Delivery Phone
            </div>
            <div className={`text-sm capitalize`}>{order.delivery_phone}</div>
          </div>
        </div>
      </div>
      <MedicationSummary medications={medications} medType={order.med_type} />
      <div className="border-t-2 border-[#ccc] pt-3">
        <div className="text-sm">
          This order has been sent to the{" "}
          <span className="font-semibold">{pharmacy.pharmacy_name}</span>{" "}
          Pharmacy:
        </div>
        <div className="flex gap-x-2 mb-1">
          <div className="text-sm font-semibold capitalize">Address:</div>
          <div className={`text-sm capitalize`}>{pharmacy.address}.</div>
          <div className="text-sm font-semibold capitalize">Phone:</div>
          <div className={`text-sm capitalize`}>{pharmacy.phone}</div>
          <div className="text-sm font-semibold capitalize">Email:</div>
          <div className={`text-sm`}>{pharmacy.email}</div>
        </div>
      </div>
      <div className="mt-10 mb-1 text-center font-bold rounded-md text-xl">
        {facility.name}
      </div>
      <div className="mb-5 text-center text-sm opacity-80">
        {facility.address1} {facility.city}, {facility.state}.{" "}
        {facility.country}. {facility.zipcode}
      </div>
    </div>
  );
});

const MedicationSummary = ({ medications, medType, orderMethod }) => {
  const medication = medications[0];
  return (
    <div className="my-4">
      {medType === "compound" ? (
        <div>
          <div className="flex gap-x-3 mb-3">
            <span className="text-2xl font-bold uppercase flex items-center gap-x-1">
              {medication.name} -
              <span className="text-sm text-gray-600">
                {medication.category}
              </span>
            </span>
          </div>
          <div className="ml-2">
            <div className="flex gap-x-2 mb-1">
              <div className="text-sm font-semibold capitalize">Size:</div>
              <div className={`text-sm capitalize`}>
                {medication.size || "-/-"}
              </div>
            </div>

            {medication.category && (
              <div className="flex gap-x-2 mb-1">
                <div className="text-sm font-semibold capitalize">
                  Category:
                </div>
                <div className={`text-sm capitalize`}>
                  {medication.category}
                </div>
              </div>
            )}
            {medication.sig && (
              <div className="flex gap-x-2 mb-1">
                <div className="text-sm font-semibold capitalize">SIG:</div>
                <div className={`text-sm capitalize`}>{medication.sig}</div>
              </div>
            )}

            {orderMethod === "Patient" ? (
              <div className="flex gap-x-2 mb-1">
                <div className="text-sm font-semibold capitalize">
                  Days of Supply:
                </div>
                <div className={`text-sm capitalize`}>
                  {medication.daysOfSupply || "-/-"}
                </div>
              </div>
            ) : (
              <div className="flex gap-x-2 mb-1">
                <div className="text-sm font-semibold capitalize">
                  Quantity:
                </div>
                <div className={`text-sm capitalize`}>
                  {medication.quantity || "-/-"}
                </div>
              </div>
            )}

            <div className="flex gap-x-2 mb-1">
              <div className="text-sm font-semibold capitalize">
                Directions:
              </div>
              <div className={`text-sm capitalize`}>
                {renderMedicationDirections(medication)}
              </div>
            </div>
            {medication.reasonForCompounding && (
              <div className="flex gap-x-2 mb-1">
                <div className="text-sm font-semibold capitalize">
                  Medical necessity justification:
                </div>
                <div className={`text-sm`}>
                  {medication.reasonForCompounding || "-/-"}
                </div>
              </div>
            )}
            {medication.additionalNote && (
              <div className="flex gap-x-2 mb-1">
                <div className="text-sm font-semibold capitalize">
                  Additional notes:
                </div>
                <div className={`text-sm`}>
                  {medication.additionalNote || "-/-"}
                </div>
              </div>
            )}
            <div className="text-lg font-bold uppercase my-3">Ingredients</div>
            {medication.ingredients.map((ingredient, index) => (
              <div key={index} className="flex gap-x-2 mb-1">
                <div className="text-sm font-semibold capitalize">
                  {ingredient.name} -{" "}
                </div>
                <div className={`text-sm capitalize`}>
                  {ingredient.quantity}
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div>
          {medications.map((med, index) => (
            <div key={index} className="mb-3">
              <div className="flex gap-x-3 mb-3">
                <span className="block text-2xl font-bold uppercase">
                  #{index + 1} <MedicationName med={med} />
                </span>
              </div>
              <div className="ml-7">
                <div className="flex gap-x-2 mb-1">
                  <div className="text-sm font-semibold capitalize">mg/mL:</div>
                  <div className={`text-sm capitalize`}>{med.mg || "-/-"}</div>
                </div>
                <div className="flex gap-x-2 mb-1">
                  <div className="text-sm font-semibold capitalize">Size:</div>
                  <div className={`text-sm capitalize`}>
                    {med.size || "-/-"}
                  </div>
                </div>
                <div className="flex gap-x-2 mb-1">
                  <div className="text-sm font-semibold capitalize">
                    Route of Admin:
                  </div>
                  <div className={`text-sm capitalize`}>
                    {med.route_of_admin || "-/-"}
                  </div>
                </div>
                <div className="flex gap-x-2 mb-1">
                  <div className="text-sm font-semibold capitalize">
                    Device:
                  </div>
                  <div className={`text-sm capitalize`}>
                    {med.device || "-/-"}
                  </div>
                </div>
                {med.form && (
                  <div className="flex gap-x-2 mb-1">
                    <div className="text-sm font-semibold capitalize">
                      Form:
                    </div>
                    <div className={`text-sm capitalize`}>{med.form}</div>
                  </div>
                )}
                {med.strength && (
                  <div className="flex gap-x-2 mb-1">
                    <div className="text-sm font-semibold capitalize">
                      Strength:
                    </div>
                    <div className={`text-sm capitalize`}>{med.strength}</div>
                  </div>
                )}

                {med.category && (
                  <div className="flex gap-x-2 mb-1">
                    <div className="text-sm font-semibold capitalize">
                      Category:
                    </div>
                    <div className={`text-sm capitalize`}>{med.category}</div>
                  </div>
                )}

                {orderMethod === "Patient" ? (
                  <div className="flex gap-x-2 mb-1">
                    <div className="text-sm font-semibold capitalize">
                      Days of Supply:
                    </div>
                    <div className={`text-sm capitalize`}>
                      {med.daysOfSupply || "-/-"}
                    </div>
                  </div>
                ) : (
                  <div className="flex gap-x-2 mb-1">
                    <div className="text-sm font-semibold capitalize">
                      Quantity:
                    </div>
                    <div className={`text-sm capitalize`}>
                      {med.quantity || "-/-"}
                    </div>
                  </div>
                )}

                <div className="flex gap-x-2 mb-1">
                  <div className="text-sm font-semibold capitalize">
                    Directions:
                  </div>
                  <div className={`text-sm capitalize`}>
                    {renderMedicationDirections(med)}
                  </div>
                </div>

                {med.reasonForCompounding && (
                  <div className="flex gap-x-2 mb-1">
                    <div className="text-sm font-semibold capitalize">
                      Medical necessity justification:
                    </div>
                    <div className={`text-sm`}>
                      {med.reasonForCompounding || "-/-"}
                    </div>
                  </div>
                )}
                {med.additionalNote && (
                  <div className="flex gap-x-2 mb-1">
                    <div className="text-sm font-semibold capitalize">
                      Additional notes:
                    </div>
                    <div className={`text-sm`}>
                      {med.additionalNote || "-/-"}
                    </div>
                  </div>
                )}
                {med.diagnosisCode && (
                  <div className="flex gap-x-2 mb-1">
                    <div className="text-sm font-semibold capitalize">
                      Last Office and Diagnosis Code:
                    </div>
                    <div className={`text-sm capitalize`}>
                      {med.diagnosisCode || "-/-"}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
